import React, { useRef, useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Typography, Card, Grid } from "@mui/material";
import { HelpOutline, ErrorOutline } from "@mui/icons-material";
import { getBackgroundModelUrlApi, getElementsDataApi, getMainModelDataApi } from "../api/model.js";
import { getTasksDataApi } from "../api/schedule.js";
import { getCommentsApi } from "../api/comment.js";
import { getNotificationsApi } from "../api/notification.js";
import { getCPISPIApi, getPercentageCompletionApi } from "../api/cost.js";
import { getContractorsApi } from "../api/member.js";
import { getMissingQTOApi } from "../api/qto.js";
import { getElementStatus, calculateGanttDateRange } from "../utils/schedule.js";
import { toUTC } from "../utils/date.js";
import { useUser } from "../hook/useUser.js";
import { useProject } from "../hook/useProject.js";
import { rotateTo, zoomTo } from "../component/bim/CameraControls.js";
import { NotificationContext } from "../component/popup/NotificationProvider";
import dayjs from "dayjs";
import ScheduleGantt from "./SchedulePage/components/ScheduleGantt.js";
import IFCViewer from "../component/bim/IFCViewer.js";
import SelectComponent from "../component/SelectComponent.js";
import NotificationList from "../component/NotificationList.js";
import Popup from "../component/popup/Popup.js";
import Container from "../component/Container.js";
import TooltipInfo from "../component/TooltipInfo.js";
import SafetyInsight from "./SafetyPage/components/SafetyInsight.js";

const SummaryCard = ({ title, count, change, color }) => (
  <Card
    sx={{
      flex: 1,
      backgroundColor: color,
      borderRadius: "10px",
      color: "#fff",
      py: 0.5,
      px: 1,
      textAlign: "center",
      height: "100%",
    }}
  >
    <Typography component="span" sx={{ fontWeight: 600, fontSize: 20 }}>
      {count}
    </Typography>
    {change !== undefined && (
      <Typography variant="subtitle1" component="span">
        （{change >= 0 && "+"}
        {change}）
      </Typography>
    )}
    <Typography sx={{ fontWeight: 600, fontSize: 12 }}>{title}</Typography>
  </Card>
);

const KPITooltip = ({ data, name }) => {
  const icon = (
    <HelpOutline
      sx={{
        position: "absolute",
        top: "6px",
        right: "15px",
        color: "#717273",
      }}
    />
  );
  const content = (
    <Grid container spacing={1} sx={{ px: 2, py: 1.5 }}>
      <Grid item xs={12}>
        Please note: For all project KPIs with parenthesis next to it.
      </Grid>
      <Grid item xs={6}>
        <SummaryCard
          title={name}
          // count={data[name]?.count}
          // change={data[name]?.change}
          count={"XXX"}
          change={"+XX"}
          color={data[name]?.color}
        />
      </Grid>
      <Grid item xs={6}>
        <Box sx={{ p: 1, border: "1px solid #D0D5DE" }}>
          <Box display="flex" alignItems="center" justifyContent="center">
            <img src="assets/illustrations/manhours.svg" alt="incidents" />
            <Typography variant="h5" gutterBottom>
              XXX
            </Typography>
            <Typography variant="body1" gutterBottom>
              &nbsp;&nbsp;(+XX)
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ textAlign: "center", whiteSpace: "nowrap" }}>
            Manhours Count
          </Typography>
        </Box>
      </Grid>
      <Grid item xs={12}>
        The first number represents the KPI's accumulated value since the project began. The value in parentheses indicates the accumulated record
        starting from Monday at 12:01 AM of the current week.
      </Grid>
    </Grid>
  );
  return <TooltipInfo icon={icon} content={content} />;
};

const ScheduleTooltip = ({ cost }) => {
  const icon = (
    <ErrorOutline
      sx={{
        position: "absolute",
        top: "-38px",
        right: 0,
        color: "#717273",
      }}
    />
  );
  const content = cost
    ? "The project completion status is calculated by Earned value / Total planned value. The figure's position is proportional to the project start and end date to reflect project schedule. Please refresh the page if page resolution is changed to view the accurate timeline."
    : "The display here is proportional to the schedule to reflect project progress. Please refresh the page if page resolution is changed to view the accurate timeline.";
  return <TooltipInfo sx={{ px: 2, py: 1, top: "-20px", right: 0 }} icon={icon} content={content} />;
};

const ProjectHomePage = () => {
  const navigate = useNavigate();
  const container = useRef();
  const hasShownQTOPopup = useRef(false);
  const { projectId } = useParams();
  const { user } = useUser(projectId);
  const projectDetails = useProject(projectId);
  const { showError, showWarning } = useContext(NotificationContext);
  const [summaryData, setSummaryData] = useState({
    "Cost Performance Index": { color: "#36BA98" },
    "Schedule Performance Index": { color: "#E76F51" },
    "Pending Safety Observation": { color: "#22668D" },
    "Pending Site Matter Observation": { color: "#A459D1" },
  });
  const [percentageCompletion, setPercentageCompletion] = useState(0);

  const [elementIds, setElementIds] = useState({
    planProgress: [],
    actualProgress: [],
    delayed: [],
    planComplete: [],
    actualComplete: [],
    showFilter: [],
    white: [],
    all: [],
  });

  const [state, setState] = useState({
    contractorOptions: [],
    contractorElementValue: [],
    viewOptions: [
      { value: "Full view", label: "Full view" },
      { value: "Actual view", label: "Actual view" },
      { value: "Plan view", label: "Plan view" },
    ],
    viewValue: "",
    backgroundOptions: [],
    backgroundValue: [],
    backgroundModels: [],
    taskTableData: [],
    filteredTaskData: [],
    startDate: dayjs(),
    endDate: dayjs(),
    currentDate: dayjs(),
    modelURL: [],
    elementsData: [],
    // isLoading: false,
    selectedRowId: "",
  });

  const [isMainLoading, setIsMainLoading] = useState(false);
  const [isTaskLoading, setIsTaskLoading] = useState(false);

  const [noticePopup, setNoticePopup] = useState(false);
  const [commonProps, setCommonProps] = useState({});
  const [notifications, setNotifications] = useState([]);

  const selectComponents = [
    {
      Component: SelectComponent,
      options: state.viewOptions,
      value: state.viewValue,
      onChange: (value) => {
        setState((prevState) => ({
          ...prevState,
          viewValue: value,
        }));
      },
      selectLabel: "View",
      placeholder: "Select an item...",
    },
    {
      Component: SelectComponent,
      options: state.contractorOptions,
      value: state.contractorElementValue,
      isMultiSelect: true,
      onChange: (value) => {
        setState((prevState) => ({
          ...prevState,
          contractorElementValue: value,
        }));
      },
      selectLabel: "Contractor",
      placeholder: "Select an item...",
    },
    {
      Component: SelectComponent,
      options: state.backgroundOptions,
      value: state.backgroundValue,
      isMultiSelect: true,
      required: false,
      onChange: (value) => {
        setState((prevState) => ({
          ...prevState,
          backgroundValue: value,
          backgroundModels: state.backgroundModels.map((backgroundModel) => {
            return {
              ...backgroundModel,
              visible: value.includes(backgroundModel.fileURL),
            };
          }),
        }));
      },
      selectLabel: "Background",
      placeholder: "Select an item...",
    },
  ];

  const timelineRef = useRef(null);

  const calculatePositionPercentage = () => {
    const totalDuration = state.endDate.diff(state.startDate, "day");
    const elapsedDuration = state.currentDate.diff(state.startDate, "day");
    return Math.max(0, Math.min(100, (elapsedDuration / totalDuration) * 100));
  };

  const calculateCircleLeftPosition = () => {
    const arrowWidth = 40;
    const circleWidth = 100;
    const timelineWidth = timelineRef.current?.offsetWidth || 0;
    const percentage = calculatePositionPercentage();

    const effectiveRangeStart = arrowWidth + circleWidth + 16;
    const effectiveRangeEnd = timelineWidth - arrowWidth - circleWidth / 2 - effectiveRangeStart - 16;
    const positionInPixels = (percentage / 100) * (effectiveRangeEnd - effectiveRangeStart) + effectiveRangeStart;

    const clampedPosition = Math.max(effectiveRangeStart, Math.min(positionInPixels, effectiveRangeEnd));
    return `${clampedPosition}px`;
  };

  //get main model url and workset
  const getMainModel = async () => {
    const result = await getMainModelDataApi(projectId);
    const data = result?.data;

    if (data && Array.isArray(data)) {
      const mainModels = data.map((d) => {
        return {
          ...d,
          fileURL: d.fileURL,
          visible: true,
        };
      });

      setState((prevState) => ({
        ...prevState,
        modelURL: mainModels,
      }));

      const uniqueWorksets = new Set(data.map((item) => item.workset));
      const workSetOptionsArray = Array.from(uniqueWorksets).map((workset) => ({
        value: workset,
        label: workset,
      }));
      setState((prevState) => ({
        ...prevState,
        workSetOptions: workSetOptionsArray,
      }));

      if (uniqueWorksets.size > 0) {
        setState((prevState) => ({
          ...prevState,
          worksetValue: Array.from(uniqueWorksets),
        }));
      }
      await getElementsData();
    }
  };

  //get background model url&filter
  const getBackgroundModelUrl = async () => {
    const result = await getBackgroundModelUrlApi(projectId);
    const data = result?.data;
    if (data && Array.isArray(data)) {
      const backgroundModels = data.map((d) => {
        return {
          ...d,
          fileURL: d.fileURL,
          visible: false,
        };
      });
      const backgroundArray = backgroundModels.map((item) => ({
        value: item.fileURL,
        label: item.filename,
      }));
      setState((prevState) => ({
        ...prevState,
        backgroundOptions: backgroundArray,
        backgroundModels: backgroundModels,
        // backgroundValue: backgroundArray.map((b) => b.value),
        backgroundValue: [],
      }));
    }
  };

  const getElementsData = async () => {
    try {
      const result = await getElementsDataApi(projectId);
      const data = result?.data;
      if (!data) showError("No element data returned from API.");

      setState((prevState) => {
        const newState = {
          ...prevState,
          elementsData: data,
        };
        colorRulesData(newState.elementsData);
        return newState;
      });
    } catch {
      showError("Received element data occurred error.");
    }
  };

  const colorRulesData = (data) => {
    const elementStatus = getElementStatus(data, state.currentDate);
    let allIds = data?.map((item) => item?.bimElementId);
    setElementIds((prevState) => ({
      ...prevState,
      ...elementStatus,
      all: allIds,
    }));
  };

  const homeViewOnClick = () => {
    if (container.current) {
      zoomTo(container.current.world.camera, container.current.mainModels);
    }
  };

  const rotateToViewOnClick = (side) => {
    rotateTo(container.current.world.camera, side);
  };

  const getTaskData = async () => {
    const result = await getTasksDataApi(projectId);
    const data = result?.data;
    if (!data || !Array.isArray(data)) {
      showError("Invalid or empty task data received.");
      return;
    }
    const { startDate, endDate } = calculateGanttDateRange(data, projectDetails);
    setState((prevState) => ({
      ...prevState,
      startDate,
      endDate,
      taskTableData: data,
    }));
  };

  const getCPISPI = async () => {
    if (user?.userRole !== "Contractor") {
      const result = await getCPISPIApi(projectId, state.currentDate.format("YYYY-MM-DD"));
      const cpiValue = result?.data?.cpi;
      const spiValue = result?.data?.spi;

      setSummaryData((prevState) => ({
        ...prevState,
        "Cost Performance Index": {
          count: cpiValue,
          color: "#36BA98",
        },
        "Schedule Performance Index": {
          count: spiValue,
          color: "#E76F51",
        },
      }));
    }
  };

  const getPercentageCompletion = async () => {
    const percentageCompletionData = await getPercentageCompletionApi(projectId);
    setPercentageCompletion(percentageCompletionData?.data);
  };

  const getObservations = async () => {
    const current = await getCommentsApi(projectId, undefined, undefined, [1, 3].join(","), 1);
    const lastWeek = await getCommentsApi(
      projectId,
      undefined,
      undefined,
      [1, 3].join(","),
      1,
      undefined,
      undefined,
      toUTC(`${dayjs(0).format("YYYY/MM/DD")} 00:00:00`).format("YYYY/MM/DD HH:mm:ss"),
      toUTC(`${dayjs().add(-7, "day").format("YYYY/MM/DD")} 23:59:59`).format("YYYY/MM/DD HH:mm:ss")
    );
    setSummaryData((prevState) => ({
      ...prevState,
      "Pending Safety Observation": {
        ...prevState["Pending Safety Observation"],
        count: current.data.filter((d) => d.commentType === "Safety Issue").length,
        change:
          current.data.filter((d) => d.commentType === "Safety Issue").length - lastWeek.data.filter((d) => d.commentType === "Safety Issue").length,
      },
      "Pending Site Matter Observation": {
        ...prevState["Pending Site Matter Observation"],
        count: current.data.filter((d) => d.commentType === "Site Matter").length,
        change:
          current.data.filter((d) => d.commentType === "Site Matter").length - lastWeek.data.filter((d) => d.commentType === "Site Matter").length,
      },
    }));
  };

  const getContractorsData = async () => {
    try {
      const result = await getContractorsApi(projectId);
      const data = result?.data;
      if (data) {
        const conTractors = new Set(data?.map((item) => item.name));
        const options = Array.from(conTractors).map((item) => ({
          label: item,
          value: item,
        }));
        setState((prevState) => ({
          ...prevState,
          contractorOptions: options,
        }));
        if (Array.from(conTractors).length > 0) {
          setState((prevState) => ({
            ...prevState,
            contractorElementValue: Array.from(conTractors),
          }));
        }
      }
    } catch {
      showError("Received contractor data occurred error.");
    }
  };

  const getNotifications = async () => {
    const response = await getNotificationsApi(projectId);
    if (response) {
      setNotifications(response.data);
    }
  };

  const getMissingQTOIDs = async () => {
    const result = await getMissingQTOApi(projectId);
    if (result && Array.isArray(result.data) && result.data.length > 0) {
      const types = result.data.join("<br />");
      let errorMessage = `<br /><strong>To ensure consistency and accuracy, please update the QTO module with the latest QTO list.<br />Below QTO IDs found in the model module are missing from the QTO module:</strong><br /><br />${types}<br />`;
      showWarning(errorMessage);
      hasShownQTOPopup.current = true;
    }
  };

  const handleRowClick = (row) => {
    setState((prevState) => ({
      ...prevState,
      selectedRowId: row.taskId,
    }));
  };

  useEffect(() => {
    if (projectId && user !== undefined && !hasShownQTOPopup.current) {
      if (user?.isPM === true) {
        getMissingQTOIDs();
      }
    }
  }, [projectId, user]);

  useEffect(() => {
    if (!projectDetails) return;
    const loadTaskAndContractors = async () => {
      setIsTaskLoading(true);
      await Promise.all([getTaskData(), getContractorsData()]);
      setIsTaskLoading(false);
    };
    loadTaskAndContractors();
  }, [projectDetails]);

  useEffect(() => {
    const loadMainContent = async () => {
      setIsMainLoading(true);
      await Promise.all([
        getNotifications(),
        getObservations(),
        getCPISPI(),
        getPercentageCompletion(),
      ]);
      setIsMainLoading(false);
    };
    if (user) loadMainContent();
  }, [user]);

  useEffect(() => {
    const loadModel = async () => {
      await Promise.all([getMainModel(), getBackgroundModelUrl()]);
      setState((prev) => ({
        ...prev,
        viewValue: "Actual view",
      }));
    };
    if (user) loadModel();
  }, [user]);

  // useEffect(() => {
  //   const fetchData = async () => {
  //     await Promise.all([getMainModel(), getBackgroundModelUrl(), getCPISPI(), getPercentageCompletion(), getObservations(), getNotifications()]);
  //     setState((prevState) => ({
  //       ...prevState,
  //       viewValue: "Actual view",
  //       isLoading: false,
  //     }));
  //   };
  //   if (user) {
  //     setState((prevState) => ({
  //       ...prevState,
  //       isLoading: true,
  //     }));
  //     fetchData();
  //   }
  // }, [user]);

  useEffect(() => {
    if (state.viewValue === "Actual view") {
      setCommonProps(() => ({
        showFilterElementIds: elementIds.showFilter,
        allElementIds: elementIds.all,
        highlightElementIds: [],
        hideElementIds: elementIds.actualHide,
        delayedNotStartElementIds: elementIds.delayedNotStart,
        inProgressElementIds: elementIds.actualProgress,
        delayedElementIds: elementIds.delayed,
      }));
    } else if (state.viewValue === "Plan view") {
      setCommonProps(() => ({
        showFilterElementIds: elementIds.showFilter,
        allElementIds: elementIds.all,
        highlightElementIds: [],
        hideElementIds: elementIds.planHide,
        inProgressElementIds: elementIds.planProgress,
      }));
    } else {
      setCommonProps(() => ({
        showFilterElementIds: elementIds.showFilter,
        allElementIds: elementIds.all,
        highlightElementIds: [],
        hideElementIds: [],
        delayedNotStartElementIds: [],
        inProgressElementIds: [],
        delayedElementIds: [],
      }));
    }
  }, [state.viewValue]);

  return (
    <>
      <Grid container spacing={2} sx={{ flexWrap: "nowrap" }}>
        <Grid item xs={7} sx={{ minWidth: 700 }}>
          <Box sx={{ height: "calc(50vh - 62px)", width: "100%" }}>
            <IFCViewer
              ref={container}
              modelURL={state.modelURL}
              backgroundModels={state.backgroundModels}
              title={"3D Model View"}
              buttonIcon="assets/icons/arrowdouble.svg"
              buttonOnClick={() => navigate(`/modules/3DModel/${projectId}`)}
              selectComponents={selectComponents}
              homeViewOnClick={homeViewOnClick}
              rotateToViewOnClick={rotateToViewOnClick}
              advanced
              {...commonProps}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              height: "calc(50vh - 62px)",
              maxWidth: "100%",
              mt: 2,
            }}
          >
            <ScheduleGantt
              tasks={state.taskTableData}
              startDate={state.startDate}
              endDate={state.endDate}
              currentDate={state.currentDate}
              buttonOnClick={() => navigate(`/modules/Schedule/${projectId}`)}
              buttonIcon="assets/icons/arrowdouble.svg"
              isLoading={isTaskLoading && state.taskTableData.length === 0}
              selectedRowId={state.selectedRowId}
              handleRowClick={handleRowClick}
              tableRowHeight={24.65}
              estimateSize={24.65}
            ></ScheduleGantt>
          </Box>
        </Grid>
        <Grid item xs={5} sx={{ minWidth: 630 }}>
          <Box sx={{ display: "flex", gap: 2, flexDirection: "column" }}>
            <Container
              title="Notification"
              icon="assets/icons/notification.svg"
              buttonIcon="assets/icons/whiteexpand.svg"
              onClick={() => setNoticePopup(true)}
              isLoading={isMainLoading && notifications.length === 0}
              sx={{ height: "calc(50vh - 200px)" }}
            >
              <NotificationList notifications={notifications} />
            </Container>
            <Container title="Summary" icon="assets/icons/summary.svg" isLoading={isMainLoading} sx={{ height: 225 }}>
              <KPITooltip data={summaryData} name="Pending Site Matter Observation" />
              <Grid container spacing={1} sx={{ pt: 2, px: 2, justifyContent: "center" }}>
                {Object.keys(summaryData).map(
                  (key) =>
                    summaryData[key].count !== undefined && (
                      <Grid key={key} item xs={3}>
                        <SummaryCard title={key} count={summaryData[key].count} change={summaryData[key].change} color={summaryData[key].color} />
                      </Grid>
                    )
                )}
              </Grid>
              <Box sx={{ position: "relative", pt: "50px", px: 2 }}>
                <Box
                  ref={timelineRef}
                  sx={{
                    height: "10px",
                    background: "#BBBBBB",
                    position: "relative",
                  }}
                >
                  <img src={"assets/illustrations/greyarrow.svg"} style={{ position: "absolute", top: "-15px", zIndex: 1 }} alt="greyarrow" />
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#fff",
                      position: "absolute",
                      top: "-9px",
                      left: "20px",
                      zIndex: 1,
                    }}
                  >
                    {dayjs(projectDetails?.startDate).format("DD MMM YYYY")}
                  </Typography>
                  <Typography variant="body2" sx={{ position: "absolute", top: "-35px", left: "2px" }}>
                    Project Start Date
                  </Typography>
                  <Box
                    sx={{
                      zIndex: 2,
                      position: "absolute",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexDirection: "column",
                      width: "90px",
                      height: "90px",
                      borderRadius: "50%",
                      border: "3px solid #5B5B5B",
                      top: "-37px",
                      backgroundColor: "#fff",
                      margin: "auto",
                      left: calculateCircleLeftPosition(),
                    }}
                  >
                    <Typography variant="h6" sx={{ textAlign: "center", lineHeight: 1.2 }}>
                      {user && user?.userRole !== "Contractor" ? `${percentageCompletion}%` : `${state.currentDate.format("DD MMM")}`}
                    </Typography>
                    {user && user?.userRole !== "Contractor" && (
                      <Typography variant="h6" sx={{ textAlign: "center", lineHeight: 1.2, fontSize: 14 }}>
                        Completion
                      </Typography>
                    )}
                    <Typography variant="body1" sx={{ textAlign: "center", fontSize: user?.userRole !== "Contractor" ? 10 : 16 }}>
                      {user && user?.userRole !== "Contractor" ? state.currentDate.format("DD MMM YYYY") : state.currentDate.format("YYYY")}
                    </Typography>
                  </Box>
                  <img
                    src={"assets/illustrations/greyarrow2.svg"}
                    style={{
                      position: "absolute",
                      top: "-15px",
                      right: 0,
                      zIndex: 1,
                    }}
                    alt="greyarrow"
                  />
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#fff",
                      position: "absolute",
                      top: "-9px",
                      right: "20px",
                      zIndex: 1,
                    }}
                  >
                    {dayjs(projectDetails?.endDate).format("DD MMM YYYY")}
                  </Typography>
                  <Typography variant="body2" sx={{ position: "absolute", top: "-35px", right: "22px" }}>
                    Project End Date
                  </Typography>
                  <ScheduleTooltip cost={user?.userRole !== "Contractor"} />
                </Box>
              </Box>
            </Container>
            <Container
              title="Safety Insight"
              icon="assets/icons/safety.svg"
              buttonIcon="assets/icons/arrowdouble.svg"
              onClick={() => navigate(`/modules/Safety/${projectId}`)}
              isLoading={isMainLoading}
              sx={{ height: "calc(50vh - 165px)" }}
              overflow="hidden"
            >
              <KPITooltip data={summaryData} name="Pending Site Matter Observation" />
              <SafetyInsight />
            </Container>
          </Box>
        </Grid>
      </Grid>
      <Popup
        title="Notification"
        open={noticePopup}
        setOpen={setNoticePopup}
        onClose={() => setNoticePopup(false)}
        modalStyle={{
          "& .MuiBox-root.css-ft9d2q": {
            borderRadius: "10px 10px 0 0",
          },
        }}
        popupStyle={{
          minWidth: 750,
        }}
      >
        <Box sx={{ py: 2, px: 4, maxHeight: "500px", overflow: "auto" }}>
          <NotificationList notifications={notifications} />
        </Box>
      </Popup>
    </>
  );
};

export default ProjectHomePage;
